import { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SchoolData, PeriodConfig, PeriodTime, RegistrationConfig, DEFAULT_DEPARTMENTS } from '@/lib/onboarding-store';
import { WizardStepLayout } from './WizardStepLayout';
import { parseSchoolTimetable } from '@/lib/api';
import { isDemoMode, showDemoLock } from '@/lib/demo/store';
import {
  Clock, BookOpen, Plus, X, School, LogIn, LogOut,
  CalendarClock, Camera, Loader2, AlertCircle, Monitor, Minus,
  CheckCircle2, AlertTriangle, Lock,
} from 'lucide-react';
import { HelpTooltip } from '@/components/ui/HelpTooltip';

interface SchoolStep3ConfigureProps {
  data: Partial<SchoolData>;
  onChange: (field: keyof SchoolData, value: unknown) => void;
  onNext: () => void;
  onBack: () => void;
  onReturnToReview?: () => void;
}

const SCHOOL_TYPE_OPTIONS = [
  { id: 'nursery',    label: 'Nursery',             years: ['Nursery'] },
  { id: 'reception',  label: 'Reception',            years: ['Reception'] },
  { id: 'primary',    label: 'Primary',              years: ['Year 1','Year 2','Year 3','Year 4','Year 5','Year 6'] },
  { id: 'secondary',  label: 'Secondary',            years: ['Year 7','Year 8','Year 9','Year 10','Year 11'] },
  { id: 'sixth-form', label: 'Sixth Form / College', years: ['Year 12','Year 13'] },
  { id: 'other',      label: 'Other',                years: [] },
];

const SUB_STEPS = [
  { label: 'School Type', icon: School },
  { label: 'School Day',  icon: Clock },
  { label: 'Subjects',    icon: BookOpen },
  { label: 'Guest Login', icon: Monitor },
];

// ── Time helpers ──────────────────────────────────────────────────────
function parseTime(t: string): number {
  const [h, m] = (t || '00:00').split(':').map(Number);
  return h * 60 + m;
}

function formatTime(mins: number): string {
  const h = Math.floor(Math.max(0, mins) / 60) % 24;
  const m = Math.max(0, mins) % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// ── Simple sequential schedule builder ───────────────────────────────
interface ScheduleSlot {
  name: string;
  startTime: string;
  endTime: string;
  type: 'reg' | 'period' | 'break' | 'lunch';
}

/**
 * Builds a display-ready schedule slot list and canonical period list.
 *
 * Two modes:
 *  • Scan mode  — when scannedPeriods is non-empty, each period's start/end
 *                 comes directly from the AI-extracted clock times. Break and
 *                 lunch are still inserted at the configured positions using
 *                 the override anchors. This handles schools with variable
 *                 period lengths (e.g. primary schools with 10–60 min slots).
 *  • Formula mode — when scannedPeriods is absent, periods are computed from
 *                 firstLessonStart + uniform periodDuration + transferTime.
 *                 This is the manual/default path.
 */
function buildSchedule(
  firstLessonStart: string,
  periodDuration: number,
  transferTime: number,
  numberOfPeriods: number,
  breakAfterPeriod: number,
  breakDurationMins: number,
  lunchAfterPeriod: number,
  lunchDurationMins: number,
  registration: RegistrationConfig,
  lunchStartOverride?: string,
  breakStartOverride?: string,
  scannedPeriods?: PeriodTime[],
): { slots: ScheduleSlot[]; periods: PeriodTime[]; breakTime: { startTime: string; endTime: string }; lunchTime: { startTime: string; endTime: string } } {
  const slots: ScheduleSlot[] = [];
  const periods: PeriodTime[] = [];
  let cur = parseTime(firstLessonStart);

  let breakTimeResult = { startTime: '10:30', endTime: '10:45' };
  let lunchTimeResult = { startTime: '12:30', endTime: '13:15' };
  let pmRegInserted = false;

  // Registration AM — applied in both modes
  if (registration.enabled) {
    const regStart = parseTime(registration.morningStartTime);
    const regEnd   = regStart + registration.morningDuration;
    slots.push({ name: registration.name, startTime: formatTime(regStart), endTime: formatTime(regEnd), type: 'reg' });
    cur = Math.max(cur, regEnd + transferTime);
  }

  if (scannedPeriods && scannedPeriods.length > 0) {
    // ── Scan mode: use actual clock times read from the timetable image ──
    for (let i = 0; i < scannedPeriods.length; i++) {
      const p = scannedPeriods[i];
      const periodNum = i + 1;
      const slotName = p.name || `Period ${periodNum}`;
      slots.push({ name: slotName, startTime: p.startTime, endTime: p.endTime, type: 'period' });
      periods.push({ name: slotName, startTime: p.startTime, endTime: p.endTime });

      if (breakAfterPeriod > 0 && periodNum === breakAfterPeriod) {
        const bStart = breakStartOverride ? parseTime(breakStartOverride) : parseTime(p.endTime) + transferTime;
        const bEnd   = bStart + breakDurationMins;
        slots.push({ name: 'Break', startTime: formatTime(bStart), endTime: formatTime(bEnd), type: 'break' });
        breakTimeResult = { startTime: formatTime(bStart), endTime: formatTime(bEnd) };
      } else if (lunchAfterPeriod > 0 && periodNum === lunchAfterPeriod) {
        const lStart = lunchStartOverride ? parseTime(lunchStartOverride) : parseTime(p.endTime) + transferTime;
        const lEnd   = lStart + lunchDurationMins;
        slots.push({ name: 'Lunch', startTime: formatTime(lStart), endTime: formatTime(lEnd), type: 'lunch' });
        lunchTimeResult = { startTime: formatTime(lStart), endTime: formatTime(lEnd) };
        if (registration.enabled && registration.afternoonEnabled) {
          const pmStart = Math.max(lEnd, parseTime(registration.afternoonStartTime));
          const pmEnd   = pmStart + registration.afternoonDuration;
          slots.push({ name: `PM ${registration.name}`, startTime: formatTime(pmStart), endTime: formatTime(pmEnd), type: 'reg' });
          pmRegInserted = true;
        }
      }
    }
  } else {
    // ── Formula mode: compute from start time + uniform duration ─────────
    for (let i = 1; i <= numberOfPeriods; i++) {
      const pStart = cur;
      let pEnd = cur + periodDuration;
      if (breakAfterPeriod > 0 && i === breakAfterPeriod && breakStartOverride) {
        pEnd = Math.min(pEnd, parseTime(breakStartOverride));
      }
      if (lunchAfterPeriod > 0 && i === lunchAfterPeriod && lunchStartOverride) {
        pEnd = Math.min(pEnd, parseTime(lunchStartOverride));
      }
      slots.push({ name: `Period ${i}`, startTime: formatTime(pStart), endTime: formatTime(pEnd), type: 'period' });
      periods.push({ name: `Period ${i}`, startTime: formatTime(pStart), endTime: formatTime(pEnd) });
      cur = pEnd;

      if (breakAfterPeriod > 0 && i === breakAfterPeriod) {
        const bStart = breakStartOverride ? parseTime(breakStartOverride) : cur + transferTime;
        const bEnd   = bStart + breakDurationMins;
        slots.push({ name: 'Break', startTime: formatTime(bStart), endTime: formatTime(bEnd), type: 'break' });
        breakTimeResult = { startTime: formatTime(bStart), endTime: formatTime(bEnd) };
        cur = bEnd + transferTime;
      } else if (lunchAfterPeriod > 0 && i === lunchAfterPeriod) {
        const lStart = lunchStartOverride ? parseTime(lunchStartOverride) : cur + transferTime;
        const lEnd   = lStart + lunchDurationMins;
        slots.push({ name: 'Lunch', startTime: formatTime(lStart), endTime: formatTime(lEnd), type: 'lunch' });
        lunchTimeResult = { startTime: formatTime(lStart), endTime: formatTime(lEnd) };
        if (registration.enabled && registration.afternoonEnabled) {
          const configuredPm = parseTime(registration.afternoonStartTime);
          const pmStart = Math.max(lEnd, configuredPm);
          const pmEnd   = pmStart + registration.afternoonDuration;
          slots.push({ name: `PM ${registration.name}`, startTime: formatTime(pmStart), endTime: formatTime(pmEnd), type: 'reg' });
          cur = pmEnd;
          pmRegInserted = true;
        } else {
          cur = lEnd + transferTime;
        }
      } else if (i < numberOfPeriods) {
        cur += transferTime;
      }
    }
  }

  // PM Registration fallback — only reached when no lunch is configured.
  if (registration.enabled && registration.afternoonEnabled && !pmRegInserted) {
    const pmStart = parseTime(registration.afternoonStartTime);
    const pmEnd   = pmStart + registration.afternoonDuration;
    const pmSlot: ScheduleSlot = {
      name: `PM ${registration.name}`,
      startTime: formatTime(pmStart),
      endTime:   formatTime(pmEnd),
      type: 'reg',
    };
    const insertIdx = slots.findIndex(s => parseTime(s.startTime) > pmStart);
    if (insertIdx === -1) slots.push(pmSlot);
    else slots.splice(insertIdx, 0, pmSlot);
  }

  return { slots, periods, breakTime: breakTimeResult, lunchTime: lunchTimeResult };
}

const DEFAULT_REGISTRATION: RegistrationConfig = {
  enabled: true,
  name: 'Registration',
  morningStartTime: '08:30',
  morningDuration: 15,
  afternoonEnabled: false,
  afternoonStartTime: '13:30',
  afternoonDuration: 10,
};

// ── Period overlap checker ────────────────────────────────────────────
// Returns human-readable error strings for every pair of slots that overlap.
function detectScheduleOverlaps(slots: ScheduleSlot[]): string[] {
  const errors: string[] = [];
  for (let i = 0; i < slots.length; i++) {
    for (let j = i + 1; j < slots.length; j++) {
      const a = slots[i];
      const b = slots[j];
      if (parseTime(a.startTime) < parseTime(b.endTime) && parseTime(a.endTime) > parseTime(b.startTime)) {
        errors.push(`"${a.name}" (${a.startTime}–${a.endTime}) overlaps with "${b.name}" (${b.startTime}–${b.endTime})`);
      }
    }
  }
  return errors;
}

// ── Subject fuzzy-overlap helper ──────────────────────────────────────
// Returns the existing subject that is a near-duplicate of candidate, or null.
// "Art" and "Art and Design" overlap because all words of "Art" appear in "Art and Design".
function findSubjectOverlap(candidate: string, existing: string[]): string | null {
  const STOP = new Set(['and', 'or', 'the', 'of', 'in', 'for', '&', 'a', 'an', 'to', 'with']);
  const words = (s: string) =>
    new Set(s.toLowerCase().split(/[\s,&/+]+/).filter(w => w.length > 1 && !STOP.has(w)));

  const candWords = words(candidate);
  for (const ex of existing) {
    if (ex.toLowerCase() === candidate.toLowerCase()) return ex;
    const exWords = words(ex);
    const [shorter, longer] =
      candWords.size <= exWords.size ? [candWords, exWords] : [exWords, candWords];
    if (shorter.size > 0 && [...shorter].every(w => longer.has(w))) return ex;
  }
  return null;
}

/**
 * Timetable scanning is off in demo mode.
 *
 * It sends a real timetable image to the AI parser and then rewrites every
 * period time from the result — so in the demo it would either burn a real AI
 * call on a fixture, or silently overwrite the seeded school day the rest of the
 * walkthrough depends on. The card dulls and the button explains rather than
 * disappearing, so the feature is still visible to a buyer evaluating it.
 */
function blockTimetableScanInDemo(): void {
  showDemoLock({
    emoji: '🗓️',
    title: 'Timetable scanning is off in the demo',
    message:
      "Scanning reads a real timetable with AI and rewrites every period time from it. The demo school already has a full timetable filled in below, edit any of the times by hand to see how it works.",
  });
}

export function SchoolStep3Configure({ data, onChange, onNext, onBack, onReturnToReview }: SchoolStep3ConfigureProps) {
  const [subStep, setSubStep]         = useState(0);
  const scanBlocked = isDemoMode();
  const [newSubject, setNewSubject]   = useState('');
  const [newDepartment, setNewDepartment] = useState('');
  const [newCustomYear, setNewCustomYear] = useState('');
  const [editingSlotIdx, setEditingSlotIdx] = useState<number | null>(null);
  const [editStart, setEditStart]     = useState('');
  const [editEnd, setEditEnd]         = useState('');

  // Seed departments with defaults the first time the user reaches sub-step 2
  useEffect(() => {
    if (subStep === 2 && data.departments === undefined) {
      onChange('departments', [...DEFAULT_DEPARTMENTS]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subStep]);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError]     = useState('');
  const [scanSummary, setScanSummary] = useState<{ extracted: string[]; missing: string[] } | null>(null);
  // Index of the week tab that started the current scan — used to keep the
  // "Scanning…" indicator, summary, and missing-field warnings on THAT tab only.
  // A single upload can still populate multiple weeks; the user just sees feedback
  // on the tab they uploaded from.
  const [scanInitiatedFromWeek, setScanInitiatedFromWeek] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Track what the last scan produced so we can wipe it on re-scan
  const [scanSubjects, setScanSubjects] = useState<string[]>([]);
  const [scanRooms,    setScanRooms]    = useState<string[]>([]);
  // Fields the scan couldn't detect — cleared individually when user manually edits
  const [scanMissingFields, setScanMissingFields] = useState<Set<string>>(new Set());

  // Draft state for duration inputs — decouples display from applied value so the
  // user can clear and retype freely; validation/clamping only fires on blur.
  const [breakDurationDraft, setBreakDurationDraft] = useState<string>(() => String(data.periodConfig?.breakDurationMins ?? 15));
  const [lunchDurationDraft, setLunchDurationDraft] = useState<string>(() => String(data.periodConfig?.lunchDurationMins ?? 45));
  const [periodDurationDraft, setPeriodDurationDraft] = useState<string>(() => String(data.periodConfig?.periodDuration ?? 60));

  // Keep drafts in sync when the value changes externally (e.g. timetable scan)
  useEffect(() => { setBreakDurationDraft(String(data.periodConfig?.breakDurationMins ?? 15)); }, [data.periodConfig?.breakDurationMins]);
  useEffect(() => { setLunchDurationDraft(String(data.periodConfig?.lunchDurationMins ?? 45)); }, [data.periodConfig?.lunchDurationMins]);
  useEffect(() => { setPeriodDurationDraft(String(data.periodConfig?.periodDuration ?? 60)); }, [data.periodConfig?.periodDuration]);

  // ── Data shortcuts ─────────────────────────────────────────────────
  const subjects         = data.subjects        || [];
  const schoolTypes      = data.schoolTypes     || [];
  const customYearGroups = data.customYearGroups || [];

  // Multi-week timetable state.
  //   activeWeekIdx = 0 → editing Week 1 (writes to data.periodConfig)
  //   activeWeekIdx > 0 → editing Week N (writes to data.additionalWeeks[idx-1])
  // No hard cap — quick-picks cover 1-4, "More" lets the admin type any number ≥ 5.
  const weekPattern = Math.max(1, data.weekPattern ?? 1);
  const [activeWeekIdx, setActiveWeekIdx] = useState(0);
  const [showMoreWeeks, setShowMoreWeeks] = useState(false);
  // Keep activeWeekIdx in range if the user reduces the pattern
  useEffect(() => { if (activeWeekIdx >= weekPattern) setActiveWeekIdx(0); }, [weekPattern, activeWeekIdx]);

  // The missing-field amber warnings come from the most recent scan. They only
  // make sense on the week tab the scan was initiated from — switching tabs
  // shouldn't carry the warnings over to a week the user never scanned for.
  // Declared AFTER activeWeekIdx because the useMemo dep array references it
  // and `const` bindings can't be touched before their declaration line runs.
  const displayedMissingFields = useMemo(
    () => (scanInitiatedFromWeek === null || scanInitiatedFromWeek === activeWeekIdx)
      ? scanMissingFields
      : new Set<string>(),
    [scanInitiatedFromWeek, activeWeekIdx, scanMissingFields],
  );

  const DEFAULT_PC: PeriodConfig = {
    numberOfPeriods: 6,
    periods: [],
    periodDuration: 60,
    schoolStartTime: '08:45',
    breakTime:  { startTime: '10:30', endTime: '10:45' },
    lunchTime:  { startTime: '12:30', endTime: '13:15' },
  };
  const periodConfig: PeriodConfig = activeWeekIdx === 0
    ? (data.periodConfig || DEFAULT_PC)
    : (data.additionalWeeks?.[activeWeekIdx - 1] || data.periodConfig || DEFAULT_PC);

  // Whether THIS week tab has been touched by the user — uploaded a
  // timetable, clicked "Add manually", or edited any setting. While
  // false the schedule preview pills are hidden and an empty-state
  // CTA shows instead — otherwise the user lands on a default
  // formula-generated schedule whose break time clashes with the
  // period times, which reads as "broken out of the box".
  const hasUserConfigured = activeWeekIdx === 0
    ? !!data.periodConfig
    : !!data.additionalWeeks?.[activeWeekIdx - 1];

  // Writes the given PeriodConfig back to the active week slot.
  const setActiveWeekConfig = (next: PeriodConfig) => {
    if (activeWeekIdx === 0) {
      onChange('periodConfig', next);
    } else {
      const arr = [...(data.additionalWeeks || [])];
      while (arr.length < weekPattern - 1) arr.push({ ...DEFAULT_PC });
      arr[activeWeekIdx - 1] = next;
      onChange('additionalWeeks', arr.slice(0, weekPattern - 1));
    }
  };

  const firstLessonStart  = periodConfig.schoolStartTime || '08:45';
  const periodDuration    = periodConfig.periodDuration  || 60;
  const transferTime      = periodConfig.transferTime    ?? 5;
  const numberOfPeriods   = periodConfig.numberOfPeriods || 6;
  const breakAfterPeriod  = periodConfig.breakAfterPeriod  ?? 2;
  const breakDurationMins = periodConfig.breakDurationMins ?? 15;
  const lunchAfterPeriod  = periodConfig.lunchAfterPeriod  ?? 4;
  const lunchDurationMins = periodConfig.lunchDurationMins ?? 45;
  const registration      = periodConfig.registration ?? DEFAULT_REGISTRATION;

  // ── Update helper — rebuilds periods whenever params change ──────────
  const updatePc = (updates: Partial<PeriodConfig>) => {
    const next = { ...periodConfig, ...updates };
    // When the user manually changes fundamental period timing, discard scan
    // periods and recompute from the formula so the schedule stays consistent.
    const resetsFormula = 'numberOfPeriods' in updates || 'periodDuration' in updates || 'schoolStartTime' in updates;
    const scannedPeriods = resetsFormula ? undefined : (periodConfig.periods?.length ? periodConfig.periods : undefined);
    if (resetsFormula) { next.lunchStartOverride = undefined; next.breakStartOverride = undefined; }
    const { slots: _slots, periods, breakTime, lunchTime } = buildSchedule(
      next.schoolStartTime || '08:45',
      next.periodDuration  || 60,
      next.transferTime    ?? 5,
      next.numberOfPeriods || 6,
      next.breakAfterPeriod  ?? 2,
      next.breakDurationMins ?? 15,
      next.lunchAfterPeriod  ?? 4,
      next.lunchDurationMins ?? 45,
      next.registration ?? DEFAULT_REGISTRATION,
      next.lunchStartOverride,
      next.breakStartOverride,
      scannedPeriods,
    );
    setActiveWeekConfig({ ...next, periods, breakTime, lunchTime });
    // The flat period name list (`data.periods`) only mirrors Week 1 — it's used by
    // legacy code paths that haven't been migrated to the multi-week model yet.
    if (activeWeekIdx === 0) onChange('periods', periods.map(p => p.name));
  };

  const updateRegistration = (updates: Partial<RegistrationConfig>) => {
    updatePc({ registration: { ...registration, ...updates } });
  };

  // ── Inline slot time editor — applies edits back to periodConfig ──────
  const applySlotEdit = (slotIdx: number, slots: ScheduleSlot[], newStart: string, newEnd: string) => {
    if (!newStart || !newEnd || parseTime(newEnd) <= parseTime(newStart)) return;
    const slot = slots[slotIdx];
    const dur = parseTime(newEnd) - parseTime(newStart);

    if (slot.type === 'period') {
      // Count period slots before this one to derive its index in the periods array
      let periodIdx = 0;
      for (let i = 0; i < slotIdx; i++) { if (slots[i].type === 'period') periodIdx++; }
      // Capture current periods (or derive from formula slots so edit switches to scan mode)
      const base: PeriodTime[] = periodConfig.periods?.length
        ? periodConfig.periods
        : slots.filter(s => s.type === 'period').map(s => ({ name: s.name, startTime: s.startTime, endTime: s.endTime }));
      const updated = base.map((p, i) => i === periodIdx ? { ...p, startTime: newStart, endTime: newEnd } : p);
      const next = { ...periodConfig, periods: updated };
      const { periods: ps, breakTime, lunchTime } = buildSchedule(
        next.schoolStartTime || '08:45', next.periodDuration || 60, next.transferTime ?? 5,
        next.numberOfPeriods || 6, next.breakAfterPeriod ?? 2, next.breakDurationMins ?? 15,
        next.lunchAfterPeriod ?? 4, next.lunchDurationMins ?? 45,
        next.registration ?? DEFAULT_REGISTRATION,
        next.lunchStartOverride, next.breakStartOverride, updated,
      );
      setActiveWeekConfig({ ...next, periods: ps, breakTime, lunchTime });
      if (activeWeekIdx === 0) onChange('periods', ps.map(p => p.name));
    } else if (slot.type === 'break') {
      updatePc({ breakStartOverride: newStart, breakDurationMins: Math.max(5, dur) });
    } else if (slot.type === 'lunch') {
      updatePc({ lunchStartOverride: newStart, lunchDurationMins: Math.max(15, dur) });
    } else if (slot.type === 'reg') {
      if (slot.name.startsWith('PM')) {
        updateRegistration({ afternoonStartTime: newStart, afternoonDuration: Math.max(5, dur) });
      } else {
        updateRegistration({ morningStartTime: newStart, morningDuration: Math.max(5, dur) });
      }
    }
  };

  // ── Computed schedule for display ─────────────────────────────────
  const { slots: scheduleSlots } = buildSchedule(
    firstLessonStart, periodDuration, transferTime, numberOfPeriods,
    breakAfterPeriod, breakDurationMins, lunchAfterPeriod, lunchDurationMins,
    registration,
    periodConfig.lunchStartOverride,
    periodConfig.breakStartOverride,
    periodConfig.periods?.length ? periodConfig.periods : undefined,
  );

  // Detect all schedule overlaps — used for inline warning + blocking validation
  const scheduleOverlapErrors = detectScheduleOverlaps(scheduleSlots);

  // ── Year group helpers ─────────────────────────────────────────────
  const derivedYearGroups = (() => {
    const years: string[] = [];
    for (const typeId of schoolTypes) {
      const option = SCHOOL_TYPE_OPTIONS.find(o => o.id === typeId);
      if (option) years.push(...option.years);
    }
    years.push(...customYearGroups);
    return years;
  })();

  const updateYearGroups = (newTypes: string[], newCustom: string[]) => {
    const years: string[] = [];
    for (const typeId of newTypes) {
      const option = SCHOOL_TYPE_OPTIONS.find(o => o.id === typeId);
      if (option) years.push(...option.years);
    }
    years.push(...newCustom);
    onChange('yearGroups', years);
  };

  const toggleSchoolType = (typeId: string) => {
    const updated = schoolTypes.includes(typeId)
      ? schoolTypes.filter(t => t !== typeId)
      : [...schoolTypes, typeId];
    onChange('schoolTypes', updated);
    updateYearGroups(updated, customYearGroups);
  };

  const addCustomYear = () => {
    if (!newCustomYear.trim() || customYearGroups.includes(newCustomYear.trim())) return;
    const updated = [...customYearGroups, newCustomYear.trim()];
    onChange('customYearGroups', updated);
    updateYearGroups(schoolTypes, updated);
    setNewCustomYear('');
  };

  const removeCustomYear = (index: number) => {
    const updated = customYearGroups.filter((_, i) => i !== index);
    onChange('customYearGroups', updated);
    updateYearGroups(schoolTypes, updated);
  };

  // ── Subject helpers ────────────────────────────────────────────────
  const addSubject = () => {
    if (!newSubject.trim() || subjects.includes(newSubject.trim())) return;
    onChange('subjects', [...subjects, newSubject.trim()]);
    setNewSubject('');
  };

  const removeSubject = (index: number) => onChange('subjects', subjects.filter((_, i) => i !== index));

  // ── Department helpers ─────────────────────────────────────────────
  const depts = data.departments ?? DEFAULT_DEPARTMENTS;
  const addDepartment = () => {
    const v = newDepartment.trim();
    if (!v || depts.includes(v)) return;
    onChange('departments', [...depts, v]);
    setNewDepartment('');
  };
  const removeDepartment = (index: number) => onChange('departments', depts.filter((_, i) => i !== index));

  // ── Timetable scan ─────────────────────────────────────────────────
  const handleScanFile = async (file: File) => {
    setScanLoading(true);
    setScanInitiatedFromWeek(activeWeekIdx);
    setScanError('');
    setScanSummary(null);
    setScanMissingFields(new Set());
    try {
      let imageData: string;
      let mimeType: string;

      if (file.type === 'application/pdf') {
        const pdfjsLib = await import('pdfjs-dist');
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 2.0 });
        const canvas = document.createElement('canvas');
        canvas.width  = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d')!;
        await page.render({ canvasContext: ctx, viewport }).promise;
        imageData = canvas.toDataURL('image/jpeg', 0.92).split(',')[1];
        mimeType  = 'image/jpeg';
      } else {
        const reader = new FileReader();
        imageData = await new Promise((resolve, reject) => {
          reader.onload  = () => resolve((reader.result as string).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        mimeType = file.type;
      }

      const parsed = await parseSchoolTimetable(imageData, mimeType);

      // ── Evidence-based baseline ─────────────────────────────────────────
      // Every field starts as OFF/None/empty. A value is only applied when the
      // scan provides explicit evidence for it. This prevents partial scans from
      // silently leaving stale or default values in place.
      //   • breakAfterPeriod / lunchAfterPeriod start at 0 (None)
      //   • registration.enabled starts false
      //   • registration.afternoonEnabled starts false
      // Only transferTime is preserved from the user's prior manual settings.
      const newCfg: PeriodConfig = {
        numberOfPeriods: 6,
        periods: [],
        periodDuration: 60,
        schoolStartTime: '08:45',
        breakTime:  { startTime: '10:30', endTime: '10:45' },
        lunchTime:  { startTime: '12:30', endTime: '13:15' },
        // Absolute-time overrides: cleared on each scan, set below when AI confirms them.
        // These are the canonical anchor points for buildSchedule — they prevent
        // periods computed by counting from shifting lunch/break to the wrong time.
        lunchStartOverride: undefined,
        breakStartOverride: undefined,
        transferTime:      periodConfig.transferTime ?? 5,
        breakAfterPeriod:  0,   // None — only set when scan confirms a break
        breakDurationMins: 15,
        lunchAfterPeriod:  0,   // None — only set when scan confirms lunch
        lunchDurationMins: 45,
        registration: {
          ...(periodConfig.registration ?? DEFAULT_REGISTRATION),
          enabled:          false,  // Off — only on when scan confirms registration
          afternoonEnabled: false,  // Off — only on when scan confirms afternoon reg with a time
        },
      };
      const extracted: string[]    = [];
      const missing: string[]      = [];
      const missingFields          = new Set<string>();

      // ── Period count ──────────────────────────────────────────────────
      if (parsed.numberOfPeriods > 0) {
        newCfg.numberOfPeriods = parsed.numberOfPeriods;
        extracted.push(`${parsed.numberOfPeriods} teaching period${parsed.numberOfPeriods !== 1 ? 's' : ''} per day`);
      } else {
        missingFields.add('numberOfPeriods');
        missing.push('Number of periods per day, enter manually below');
      }

      // ── Period duration ───────────────────────────────────────────────
      if (parsed.periodDuration) {
        newCfg.periodDuration = parsed.periodDuration;
        extracted.push(`Period length: ${parsed.periodDuration} min`);
      } else {
        missingFields.add('periodDuration');
        missing.push('Period length, not detected, enter manually below');
      }

      // ── First lesson / school start time ──────────────────────────────
      if (parsed.schoolStartTime) {
        newCfg.schoolStartTime = parsed.schoolStartTime;
        extracted.push(`First lesson at ${parsed.schoolStartTime}`);
      } else {
        missingFields.add('schoolStartTime');
        missing.push('First lesson start time, enter manually below');
      }

      // ── Period clock times & derived schoolStartTime / periodDuration ───
      // When the AI returns absolute start/end times for periods, use those as
      // the authoritative source rather than the AI's single summarised values.
      // This handles primary schools and any timetable with unequal period lengths.
      const hasTimes = parsed.periods.some((p: { startTime?: string | null }) => p.startTime);
      if (hasTimes) {
        newCfg.periods = parsed.periods.filter(
          (p: { startTime?: string | null; endTime?: string | null }) => p.startTime && p.endTime,
        ) as PeriodTime[];
        extracted.push('Clock times for all periods');

        // Derive the most common period length from actual start/end differences.
        // This is more accurate than the AI's single "periodDuration" summary.
        const durList = newCfg.periods
          .map(p => parseTime(p.endTime) - parseTime(p.startTime))
          .filter(d => d > 0 && d <= 120);
        if (durList.length > 0) {
          const sorted = [...durList].sort((a, b) => a - b);
          const median = sorted[Math.floor(sorted.length / 2)];
          if (median !== newCfg.periodDuration) {
            newCfg.periodDuration = median;
            if (!extracted.includes(`Period length: ${median} min`)) {
              extracted.push(`Period length (derived): ${median} min`);
            }
          }
        }

        // Use first period's absolute start as schoolStartTime when registration
        // is not configured (gives buildSchedule the right anchor point).
        const firstP = newCfg.periods[0];
        if (firstP?.startTime && !newCfg.registration?.enabled) {
          newCfg.schoolStartTime = firstP.startTime;
        }
      } else {
        missing.push('Period clock times, will be calculated from start time and duration above');
      }

      // ── Break ─────────────────────────────────────────────────────────
      // Only set a non-zero breakAfterPeriod when the AI provides evidence.
      // If absent, leave at 0 (None) so the form honestly reflects the scan.
      if (parsed.breakAfterPeriod) {
        newCfg.breakAfterPeriod = parsed.breakAfterPeriod;
        extracted.push(`Break after Period ${parsed.breakAfterPeriod}`);
        if (parsed.breakTime?.startTime && parsed.breakTime?.endTime) {
          const dur = parseTime(parsed.breakTime.endTime) - parseTime(parsed.breakTime.startTime);
          newCfg.breakDurationMins = dur;
          newCfg.breakTime = parsed.breakTime as { startTime: string; endTime: string };
          newCfg.breakStartOverride = parsed.breakTime.startTime;
          extracted.push(`Break: ${parsed.breakTime.startTime}–${parsed.breakTime.endTime} (${dur} min)`);
        } else {
          missingFields.add('breakDuration');
          missing.push('Break duration, adjust below');
        }
      } else if (parsed.breakTime?.startTime && parsed.breakTime?.endTime) {
        // Derive position from period end times when only the time range is given
        const breakStart = parseTime(parsed.breakTime.startTime);
        let bap = 0;
        for (let i = 0; i < parsed.periods.length; i++) {
          if (parsed.periods[i].endTime && parseTime(parsed.periods[i].endTime!) <= breakStart) bap = i + 1;
        }
        newCfg.breakAfterPeriod  = bap || Math.floor((newCfg.numberOfPeriods || 6) / 2);
        newCfg.breakDurationMins = parseTime(parsed.breakTime.endTime) - parseTime(parsed.breakTime.startTime);
        newCfg.breakTime = parsed.breakTime as { startTime: string; endTime: string };
        newCfg.breakStartOverride = parsed.breakTime.startTime;
        extracted.push(`Break: ${parsed.breakTime.startTime}–${parsed.breakTime.endTime}`);
      } else {
        // No break evidence — stay at 0 (None); user can add manually if needed
        missingFields.add('breakConfig');
        missing.push('Break, not found in timetable; enable below if your school has one');
      }

      // ── Lunch ─────────────────────────────────────────────────────────
      if (parsed.lunchAfterPeriod) {
        newCfg.lunchAfterPeriod = parsed.lunchAfterPeriod;
        extracted.push(`Lunch after Period ${parsed.lunchAfterPeriod}`);
        if (parsed.lunchTime?.startTime && parsed.lunchTime?.endTime) {
          const dur = parseTime(parsed.lunchTime.endTime) - parseTime(parsed.lunchTime.startTime);
          newCfg.lunchDurationMins = dur;
          newCfg.lunchTime = parsed.lunchTime as { startTime: string; endTime: string };
          newCfg.lunchStartOverride = parsed.lunchTime.startTime;
          extracted.push(`Lunch: ${parsed.lunchTime.startTime}–${parsed.lunchTime.endTime} (${dur} min)`);
        } else {
          missingFields.add('lunchDuration');
          missing.push('Lunch duration, adjust below');
        }
      } else if (parsed.lunchTime?.startTime && parsed.lunchTime?.endTime) {
        const lunchStart = parseTime(parsed.lunchTime.startTime);
        let lap = 0;
        for (let i = 0; i < parsed.periods.length; i++) {
          if (parsed.periods[i].endTime && parseTime(parsed.periods[i].endTime!) <= lunchStart) lap = i + 1;
        }
        newCfg.lunchAfterPeriod  = lap || Math.floor(((newCfg.numberOfPeriods || 6) * 2) / 3);
        newCfg.lunchDurationMins = parseTime(parsed.lunchTime.endTime) - parseTime(parsed.lunchTime.startTime);
        newCfg.lunchTime = parsed.lunchTime as { startTime: string; endTime: string };
        newCfg.lunchStartOverride = parsed.lunchTime.startTime;
        extracted.push(`Lunch: ${parsed.lunchTime.startTime}–${parsed.lunchTime.endTime}`);
      } else {
        // No lunch evidence — stay at 0 (None)
        missingFields.add('lunchConfig');
        missing.push('Lunch, not found in timetable; enable below if your school has a lunch break');
      }

      // ── Registration ──────────────────────────────────────────────────
      // Enabled only when AI explicitly confirms it.
      // Afternoon registration requires BOTH the enabled flag AND an actual start
      // time — guarding against the AI speculatively setting afternoonEnabled:true.
      if (parsed.registration?.enabled) {
        const morningTimeFound = !!parsed.registration.morningStartTime;
        const morningDurFound  = !!parsed.registration.morningDuration;
        const afternoonFound   = !!(parsed.registration.afternoonEnabled && parsed.registration.afternoonStartTime);

        newCfg.registration = {
          enabled:            true,
          name:               parsed.registration.name || 'Registration',
          morningStartTime:   parsed.registration.morningStartTime || parsed.schoolStartTime || '08:30',
          morningDuration:    parsed.registration.morningDuration  || 15,
          afternoonEnabled:   afternoonFound,
          afternoonStartTime: parsed.registration.afternoonStartTime || '13:30',
          afternoonDuration:  parsed.registration.afternoonDuration  || 10,
        };

        if (morningTimeFound && morningDurFound) {
          const label = parsed.registration.name || 'Registration';
          extracted.push(`${label}: ${parsed.registration.morningStartTime}, ${parsed.registration.morningDuration} min`);
          if (afternoonFound) {
            extracted.push(`Afternoon ${label}: ${parsed.registration.afternoonStartTime}, ${parsed.registration.afternoonDuration ?? 10} min`);
          }
        } else {
          extracted.push(`${parsed.registration.name || 'Registration'} detected. Check timing below`);
          if (!morningTimeFound) { missingFields.add('registrationTime'); missing.push('Registration start time, enter manually below'); }
          if (!morningDurFound)  missing.push('Registration duration, enter manually below');
        }
      } else {
        // Scan found no registration — baseline already has enabled: false, so nothing to do.
        // Tell the user in case their school does have registration but it wasn't visible.
        missingFields.add('registrationConfig');
        missing.push('Registration, not detected; enable below if your school uses it');
      }

      // ── Subjects — strip previous scan additions, then smart-merge ────
      {
        const manualSubjects = (data.subjects || []).filter(s => !scanSubjects.includes(s));
        const newScanSubjects: string[] = [];
        const toReplace: string[] = [];

        for (const sub of (parsed.subjects ?? [])) {
          const match = findSubjectOverlap(sub, manualSubjects.filter(s => !toReplace.includes(s)));
          if (match) {
            if (sub.length > match.length) { toReplace.push(match); newScanSubjects.push(sub); }
          } else {
            newScanSubjects.push(sub);
          }
        }

        const mergedSubjects = [
          ...manualSubjects.filter(s => !toReplace.includes(s)),
          ...newScanSubjects,
        ];
        onChange('subjects', mergedSubjects);
        setScanSubjects(newScanSubjects);

        const net = newScanSubjects.length;
        if (net > 0) extracted.push(`${net} subject${net !== 1 ? 's' : ''} added`);
        if (toReplace.length > 0) extracted.push(`${toReplace.length} subject${toReplace.length !== 1 ? 's' : ''} replaced with school-specific names`);
      }

      // ── Rooms — strip previous scan additions, then merge ────────────
      {
        const manualRooms = (data.rooms || []).filter(r => !scanRooms.includes(r));
        const newScanRooms = (parsed.rooms ?? []).filter(
          r => !manualRooms.some(mr => mr.toLowerCase() === r.toLowerCase()),
        );
        const mergedRooms = [...manualRooms, ...newScanRooms];
        onChange('rooms', mergedRooms);
        setScanRooms(newScanRooms);

        const net = newScanRooms.length;
        if (net > 0) extracted.push(`${net} room${net !== 1 ? 's' : ''} added`);
      }

      setActiveWeekConfig(newCfg);
      // Mirror to flat `periods` only when editing Week 1 — legacy callers use it as the primary list.
      if (activeWeekIdx === 0 && newCfg.periods?.length) onChange('periods', newCfg.periods.map(p => p.name));

      // ── Multi-week distribution ──────────────────────────────────────
      // When a single upload contains multiple weeks (e.g. Week 1 + Week 2 on
      // one page), the AI returns the extras under `additionalWeeks`. Map each
      // entry to a fresh PeriodConfig and stash it in data.additionalWeeks so the
      // wizard's tab row reflects the right rotation length automatically.
      const extraWeeks = (parsed as typeof parsed & { additionalWeeks?: import('@/lib/api').ParsedWeek[] }).additionalWeeks;
      if (activeWeekIdx === 0 && Array.isArray(extraWeeks) && extraWeeks.length > 0) {
        const additional: PeriodConfig[] = extraWeeks.map((w) => ({
          numberOfPeriods: w.numberOfPeriods || (w.periods?.length ?? 0) || 6,
          periods: (w.periods || []).map(p => ({
            name: p.name || '',
            startTime: p.startTime || '',
            endTime: p.endTime || '',
          })),
          periodDuration: w.periodDuration || newCfg.periodDuration || 60,
          schoolStartTime: w.schoolStartTime || newCfg.schoolStartTime || '08:45',
          breakTime: {
            startTime: w.breakTime?.startTime ?? newCfg.breakTime.startTime,
            endTime: w.breakTime?.endTime ?? newCfg.breakTime.endTime,
          },
          lunchTime: {
            startTime: w.lunchTime?.startTime ?? newCfg.lunchTime.startTime,
            endTime: w.lunchTime?.endTime ?? newCfg.lunchTime.endTime,
          },
          breakAfterPeriod: w.breakAfterPeriod ?? newCfg.breakAfterPeriod ?? 0,
          breakDurationMins: w.breakDurationMins ?? newCfg.breakDurationMins ?? 15,
          lunchAfterPeriod: w.lunchAfterPeriod ?? newCfg.lunchAfterPeriod ?? 0,
          lunchDurationMins: w.lunchDurationMins ?? newCfg.lunchDurationMins ?? 45,
          // Anchor the absolute lunch/break start times when the AI gave us explicit values
          lunchStartOverride: w.lunchTime?.startTime ?? undefined,
          breakStartOverride: w.breakTime?.startTime ?? undefined,
          transferTime: periodConfig.transferTime ?? 5,
          registration: {
            ...DEFAULT_REGISTRATION,
            ...(w.registration ?? newCfg.registration ?? {}),
          } as RegistrationConfig,
        }));
        onChange('additionalWeeks', additional);
        onChange('weekPattern', 1 + additional.length);
        // Anchor Week 1 to the most recent Monday if not already set
        if (!data.weekAnchorDate) {
          const today = new Date();
          const day = today.getDay();
          const diff = day === 0 ? -6 : 1 - day;
          today.setDate(today.getDate() + diff);
          const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
          onChange('weekAnchorDate', iso);
        }
        extracted.push(`Detected ${1 + additional.length}-week rotation. Each week placed on its own tab`);
      }

      setScanMissingFields(missingFields);
      setScanSummary({ extracted, missing });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setScanError(msg.includes('API POST') ? 'Could not read the timetable. Try a clearer image or enter times manually.' : msg);
    } finally {
      setScanLoading(false);
    }
  };

  // ── Sub-step navigation ────────────────────────────────────────────
  const isLastSubStep = subStep === SUB_STEPS.length - 1;
  const handleSubNext = () => { if (isLastSubStep) onNext(); else setSubStep(s => s + 1); };
  const handleSubBack = () => { if (subStep === 0) onBack(); else setSubStep(s => s - 1); };

  // Text typed into an "add new X" field but never committed with its own
  // + Add button — only matters once Continue would actually leave this step.
  // Uncommitted "+ Add" input, scoped to the sub-step that owns the field.
  //
  // Gating this on `isLastSubStep` was wrong in both directions. Leaving the
  // Subjects sub-step with a typed-but-unadded subject said nothing at all —
  // and that is the exact moment the input is abandoned, because this component
  // stays mounted so the text survives out of sight. Then two sub-steps later,
  // on Guest Login, it finally warned about a "subject" with no such field
  // anywhere on screen. Warn when leaving the page that holds the box.
  const SUB_STEP_DRAFTS: Record<number, [label: string, value: string][]> = {
    0: [['year group', newCustomYear]],
    2: [['department', newDepartment], ['subject', newSubject]],
  };
  const pendingConfigDrafts = (SUB_STEP_DRAFTS[subStep] ?? [])
    .filter(([, value]) => value.trim())
    .map(([label, value]) => `${label} "${value.trim()}"`);
  const configDraftWarning = pendingConfigDrafts.length > 0
    ? `You typed a ${pendingConfigDrafts.join(' and ')} but never clicked + Add. It won't be saved.`
    : undefined;

  const subTitles: Record<number, { title: string; subtitle: string }> = {
    0: { title: 'School Type',              subtitle: 'What stages does your school cover?' },
    1: { title: 'School Day',               subtitle: 'Set up your timetable so teachers and cover staff know when each lesson runs' },
    2: { title: 'Subjects',                 subtitle: 'What subjects are taught at your school?' },
    3: { title: 'IT / Guest Computer Access', subtitle: 'Optional: shared classroom computer login for cover teachers' },
  };

  // ── Slot tag colour ────────────────────────────────────────────────
  const slotColour: Record<ScheduleSlot['type'], string> = {
    period: 'bg-primary/10 text-primary border-primary/20',
    break:  'bg-warning/10 text-warning-text border-warning/30 dark:bg-amber-900/30 dark:text-amber-300',
    lunch:  'bg-warning/10 text-warning-text border-warning/30 dark:bg-orange-900/30 dark:text-orange-300',
    reg:    'bg-success/10 text-success border-success/20',
  };

  return (
    <WizardStepLayout
      currentStep={4}
      totalSteps={8}
      title={subTitles[subStep].title}
      subtitle={subTitles[subStep].subtitle}
      onNext={handleSubNext}
      onBack={handleSubBack}
      nextLabel={isLastSubStep ? 'Continue' : 'Next'}
      showBack={true}
      validationErrors={subStep === 1 ? [
        ...(!data.coverDeadline ? ['Please set a cover work submission deadline.'] : []),
        ...scheduleOverlapErrors,
      ] : []}
      onReturnToReview={onReturnToReview}
      subStepInfo={`${SUB_STEPS[subStep].label} (${subStep + 1} of ${SUB_STEPS.length})`}
      unsavedDraftWarning={configDraftWarning}
    >
      {/* Sub-step progress bar */}
      <div className="flex items-center gap-1.5 mb-8">
        {SUB_STEPS.map((step, idx) => {
          const Icon = step.icon;
          const isActive   = idx === subStep;
          const isComplete = idx < subStep;
          return (
            <div key={idx} className="flex items-center gap-1.5 flex-1 min-w-0">
              <div className={`
                flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-semibold transition-all duration-300 whitespace-nowrap
                ${isActive   ? 'bg-primary text-primary-foreground shadow-sm' :
                  isComplete ? 'bg-primary/20 text-primary' :
                               'bg-muted text-muted-foreground'}
              `}>
                <Icon className="w-3 h-3 flex-shrink-0" />
                <span className="hidden sm:inline">{step.label}</span>
              </div>
              {idx < SUB_STEPS.length - 1 && (
                <div className={`h-0.5 flex-1 rounded transition-all duration-300 ${isComplete ? 'bg-primary/40' : 'bg-border'}`} />
              )}
            </div>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={subStep}
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -16 }}
          transition={{ duration: 0.2 }}
        >

          {/* ── Sub-step 0: School Type ──────────────────────────── */}
          {subStep === 0 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Select all that apply. Year groups will be set automatically.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {SCHOOL_TYPE_OPTIONS.map((option) => {
                  const isSelected = schoolTypes.includes(option.id);
                  return (
                    <button
                      key={option.id}
                      onClick={() => toggleSchoolType(option.id)}
                      className={`
                        p-3 rounded-xl border-2 text-sm font-medium transition-all duration-200
                        ${isSelected
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border bg-card text-muted-foreground hover:border-primary/30'}
                      `}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>

              <AnimatePresence>
                {schoolTypes.includes('other') && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="flex gap-2 mt-2">
                      <input
                        type="text"
                        value={newCustomYear}
                        onChange={(e) => setNewCustomYear(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addCustomYear()}
                        placeholder="Add custom year group"
                        className="input-premium flex-1"
                      />
                      <button onClick={addCustomYear} className="btn-secondary">
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    {customYearGroups.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {customYearGroups.map((year, index) => (
                          <span key={`${year}-${index}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium">
                            {year}
                            <button onClick={() => removeCustomYear(index)} className="hover:bg-primary/20 rounded-full p-0.5 transition-colors">
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {derivedYearGroups.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Year groups included:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {derivedYearGroups.map((year) => (
                      <span key={year} className="inline-flex items-center px-2.5 py-1 bg-success/10 text-success rounded-full text-xs font-medium">
                        {year}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Sub-step 1: School Day (includes Registration) ──── */}
          {subStep === 1 && (
            <div className="space-y-5">

              {/* Week pattern selector — supports 1-, 2-, 3- and 4-week rotations */}
              <div className="p-4 bg-accent/5 border border-accent/30 rounded-2xl space-y-3">
                <div className="flex items-center gap-2">
                  <CalendarClock className="w-4 h-4 text-accent-foreground flex-shrink-0" />
                  <span className="text-sm font-semibold text-foreground">Timetable rotation</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Does your school run the same timetable every week, or rotate through different weeks (e.g. Week A / Week B)?
                  Pick the rotation length, you'll be able to configure each week separately, including a different scan upload per week.
                </p>
                <div className="grid grid-cols-5 gap-2">
                  {[1, 2, 3, 4].map(n => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => {
                        onChange('weekPattern', n);
                        setShowMoreWeeks(false);
                        // Default anchor to the most recent Monday so dates start mapping immediately
                        if (n > 1 && !data.weekAnchorDate) {
                          const today = new Date();
                          const day = today.getDay();
                          const diff = day === 0 ? -6 : 1 - day;
                          today.setDate(today.getDate() + diff);
                          const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                          onChange('weekAnchorDate', iso);
                        }
                        setActiveWeekIdx(0);
                      }}
                      className={`py-2 px-1 sm:px-3 min-w-0 rounded-lg font-semibold leading-tight transition-all border-2 ${
                        weekPattern === n && !showMoreWeeks
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-card text-foreground border-border hover:border-primary/50'
                      }`}
                    >
                      {/* Stacked on purpose. Five columns at 360px leave ~38px of
                          inner width, and "weeks" needs ~40px at text-sm — so it
                          ran into the border. Splitting the number from the unit
                          removes the squeeze instead of relying on it wrapping. */}
                      <span className="block text-sm">{n}</span>
                      <span className="block text-[11px] sm:text-xs font-medium opacity-90">
                        {n === 1 ? 'week' : 'weeks'}
                      </span>
                    </button>
                  ))}
                  {/* "More" — opens an inline number input for rotations of 5+ weeks */}
                  <button
                    type="button"
                    onClick={() => {
                      setShowMoreWeeks(true);
                      if (weekPattern < 5) {
                        onChange('weekPattern', 5);
                        if (!data.weekAnchorDate) {
                          const today = new Date();
                          const day = today.getDay();
                          const diff = day === 0 ? -6 : 1 - day;
                          today.setDate(today.getDate() + diff);
                          const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                          onChange('weekAnchorDate', iso);
                        }
                      }
                      setActiveWeekIdx(0);
                    }}
                    className={`py-2 px-1 sm:px-3 min-w-0 rounded-lg font-semibold leading-tight transition-all border-2 ${
                      weekPattern >= 5 || showMoreWeeks
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-card text-foreground border-border hover:border-primary/50'
                    }`}
                  >
                    {weekPattern >= 5 ? (
                      <>
                        <span className="block text-sm">{weekPattern}</span>
                        <span className="block text-[11px] sm:text-xs font-medium opacity-90">weeks</span>
                      </>
                    ) : (
                      // "More…" measured 45px of text in a 40px box at 360px — it
                      // overflowed where the others now fit. "5+" over "weeks"
                      // matches their two-line shape and says the same thing.
                      <>
                        <span className="block text-sm">5+</span>
                        <span className="block text-[11px] sm:text-xs font-medium opacity-90">weeks</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Custom rotation length input — visible when "More" is active OR pattern is already 5+ */}
                {(showMoreWeeks || weekPattern >= 5) && (
                  <div className="flex items-end gap-2">
                    <div className="flex-1 space-y-1">
                      <label className="block text-xs font-semibold text-foreground">Rotation length (weeks)</label>
                      <input
                        type="number"
                        min={1}
                        step={1}
                        value={weekPattern}
                        onChange={(e) => {
                          const raw = parseInt(e.target.value, 10);
                          const n = Number.isFinite(raw) && raw > 0 ? raw : 1;
                          onChange('weekPattern', n);
                          // Trim additionalWeeks if shrinking
                          if (data.additionalWeeks && data.additionalWeeks.length > Math.max(0, n - 1)) {
                            onChange('additionalWeeks', data.additionalWeeks.slice(0, Math.max(0, n - 1)));
                          }
                          if (activeWeekIdx >= n) setActiveWeekIdx(0);
                        }}
                        className="input-premium text-sm"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Type any whole number. Each week gets its own period times and scan upload.
                      </p>
                    </div>
                  </div>
                )}

                {weekPattern > 1 && (
                  <>
                    <div className="space-y-2">
                      <label className="block text-xs font-semibold text-foreground">Week 1 starts on (Monday)</label>
                      <input
                        type="date"
                        value={data.weekAnchorDate || ''}
                        onChange={(e) => onChange('weekAnchorDate', e.target.value)}
                        className="input-premium text-sm"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Used to work out which week applies on any given date. Teachers see the right week's periods automatically when they pick a lesson date.
                      </p>
                    </div>

                    <div className="space-y-2 pt-2 border-t border-border/60">
                      <div className="flex items-baseline justify-between">
                        <p className="text-sm font-bold text-foreground">
                          Which week are you editing?
                        </p>
                        <span className="text-xs font-semibold text-primary">
                          Week {activeWeekIdx + 1} of {weekPattern}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Each tab below is a separate page of inputs. Click a tab to scan and configure that week's timetable.
                      </p>
                      {/* Prominent page-separator tabs — long rotations scroll horizontally. */}
                      <div className={`flex gap-2 ${weekPattern > 5 ? 'overflow-x-auto pb-2' : 'flex-wrap'}`}>
                        {Array.from({ length: weekPattern }).map((_, idx) => (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setActiveWeekIdx(idx)}
                            className={`flex-shrink-0 flex flex-col items-center justify-center min-w-[88px] px-5 py-3 rounded-t-lg text-sm font-bold transition-all border-2 ${
                              activeWeekIdx === idx
                                ? 'bg-primary text-primary-foreground border-primary border-b-primary shadow-md scale-[1.02] ring-2 ring-primary/30'
                                : 'bg-card text-foreground border-border hover:border-primary/60 hover:bg-primary/5'
                            }`}
                            aria-pressed={activeWeekIdx === idx}
                          >
                            <span className="text-[10px] uppercase tracking-wider opacity-80">Week</span>
                            <span className="text-lg leading-none">{idx + 1}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Per-week panel — when a rotation is configured, everything below sits
                  inside a visibly bordered card that flows from the active tab, and the
                  panel slides in from the right when the user switches tabs so the
                  context change is felt, not just labelled. */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={`week-panel-${activeWeekIdx}`}
                  initial={{ opacity: 0, x: 36 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -36 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  className={
                    weekPattern > 1
                      ? 'space-y-5 border-2 border-primary/40 bg-primary/[0.03] rounded-2xl p-4 shadow-sm relative'
                      : 'space-y-5'
                  }
                >
                  {weekPattern > 1 && (
                    <>
                      {/* Solid connector strip that ties the active tab to this panel */}
                      <div className="absolute -top-[2px] left-3 right-3 h-[2px] bg-primary rounded-full" />
                      <div className="-mt-1 mb-1 flex items-center gap-2">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-primary text-primary-foreground text-xs font-bold">
                          Week {activeWeekIdx + 1}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          Settings below apply only to this week
                        </span>
                      </div>
                    </>
                  )}

              {/* Timetable scan card */}
              {/* The "Scanning…" indicator, error and summary only render on the tab
                  the upload was initiated from. A single upload still populates every
                  detected week — the user just sees feedback on the tab they used. */}
              {(() => {
                const isOriginTab = scanInitiatedFromWeek === null || scanInitiatedFromWeek === activeWeekIdx;
                const showLoadingHere = scanLoading && isOriginTab;
                const showErrorHere = scanError && isOriginTab;
                const showSummaryHere = scanSummary && isOriginTab;
                return (
                  <div className={`p-4 rounded-2xl space-y-3 ${scanBlocked
                    ? 'bg-muted/40 border border-border'
                    : 'bg-primary/5 border border-primary/20'}`}>
                    <div className={`flex items-center gap-2 ${scanBlocked ? 'opacity-70' : ''}`}>
                      {scanBlocked
                        ? <Lock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        : <Camera className="w-4 h-4 text-primary flex-shrink-0" />}
                      <span className="text-sm font-semibold text-foreground">Scan your timetable</span>
                      <span className="text-xs text-muted-foreground font-normal">
                        {scanBlocked ? '(not in the demo)' : '(optional)'}
                      </span>
                    </div>
                    <p className={`text-xs text-muted-foreground ${scanBlocked ? 'opacity-70' : ''}`}>
                      {scanBlocked
                        ? 'The demo school already has a full timetable filled in below, edit any of the times by hand to see how it works.'
                        : "Upload a photo or PDF of your school timetable and we'll fill in period times automatically. If your timetable doesn't include clock times, just enter them manually below."}
                    </p>
                    <input ref={fileInputRef} type="file" accept="image/*,.pdf" className="hidden"
                      onChange={(e) => { const file = e.target.files?.[0]; if (file) handleScanFile(file); e.target.value = ''; }}
                    />
                    {/* Button is always disabled during any in-flight scan to avoid concurrent
                        uploads, but the "Scanning…" text only appears on the originating tab.
                        Non-origin tabs simply show the normal upload label so the user can tell
                        them apart at a glance. */}
                    <button
                      onClick={() => {
                        if (scanBlocked) { blockTimetableScanInDemo(); return; }
                        fileInputRef.current?.click();
                      }}
                      disabled={scanLoading && !scanBlocked}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all disabled:opacity-60 ${scanBlocked
                        ? 'bg-muted text-muted-foreground border border-border cursor-not-allowed'
                        : 'bg-primary text-primary-foreground hover:brightness-110'}`}
                    >
                      {scanBlocked
                        ? <><Lock className="w-4 h-4" />Upload timetable image or PDF</>
                        : showLoadingHere
                          ? <><Loader2 className="w-4 h-4 animate-spin" />Scanning…</>
                          : <><Camera className="w-4 h-4" />Upload timetable image or PDF</>}
                    </button>
                    {showErrorHere && (
                      <div className="flex items-start gap-2 text-destructive text-xs">
                        <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                        <span>{scanError}</span>
                      </div>
                    )}

                    {/* Scan summary — only on the tab the scan came from */}
                    {showSummaryHere && (
                      <motion.div
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-3 pt-1"
                      >
                        {scanSummary!.extracted.length > 0 && (
                          <div className="space-y-1.5">
                            <p className="text-xs font-semibold text-success flex items-center gap-1.5">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              Extracted automatically
                            </p>
                            <ul className="space-y-0.5 pl-5">
                              {scanSummary!.extracted.map((item, i) => (
                                <li key={i} className="text-xs text-success/80 list-disc">{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {scanSummary!.missing.length > 0 && (
                          <div className="space-y-1.5">
                            <p className="text-xs font-semibold text-warning-text dark:text-amber-400 flex items-center gap-1.5">
                              <AlertTriangle className="w-3.5 h-3.5" />
                              Still needs manual input
                            </p>
                            <ul className="space-y-0.5 pl-5">
                              {scanSummary!.missing.map((item, i) => (
                                <li key={i} className="text-xs text-warning-text dark:text-amber-400/80 list-disc">{item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </div>
                );
              })()}

              {/* Registration / Form time */}
              <div className={`p-4 rounded-xl border space-y-3 ${displayedMissingFields.has('registrationConfig') ? 'bg-warning/10 dark:bg-amber-900/10 border-warning/30 dark:border-amber-700/40' : 'bg-muted/30 border-border'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="text-sm font-semibold text-foreground">Registration / Form time</p>
                      {displayedMissingFields.has('registrationConfig') && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-warning/10 dark:bg-amber-900/40 text-warning-text dark:text-amber-400 text-[10px] font-medium border border-warning/30 dark:border-amber-700/40">
                          <AlertTriangle className="w-2.5 h-2.5" />
                          Not detected, enable if used
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">Roll-call before Period 1</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      updateRegistration({ enabled: !registration.enabled });
                      setScanMissingFields(prev => { const n = new Set(prev); n.delete('registrationConfig'); return n; });
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${registration.enabled ? 'bg-primary' : 'bg-muted'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${registration.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
                <AnimatePresence>
                  {registration.enabled && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden space-y-3 pt-2 border-t border-border"
                    >
                      <div className="flex items-center justify-between gap-4">
                        <label className="text-sm font-medium text-foreground">Session name</label>
                        <select
                          value={registration.name}
                          onChange={(e) => updateRegistration({ name: e.target.value })}
                          className="input-premium text-sm w-40"
                        >
                          <option>Registration</option>
                          <option>Form Time</option>
                          <option>Tutor Time</option>
                          <option>Assembly</option>
                        </select>
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <label className="text-sm font-medium text-foreground flex items-center gap-1">
                          Morning start
                          {displayedMissingFields.has('registrationTime') && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-warning/10 dark:bg-amber-900/40 text-warning-text dark:text-amber-400 text-[10px] font-medium border border-warning/30 dark:border-amber-700/40">
                              <AlertTriangle className="w-2.5 h-2.5" />
                              Not detected
                            </span>
                          )}
                        </label>
                        <input
                          type="time"
                          value={registration.morningStartTime}
                          onChange={(e) => {
                            updateRegistration({ morningStartTime: e.target.value });
                            setScanMissingFields(prev => { const n = new Set(prev); n.delete('registrationTime'); return n; });
                          }}
                          onClick={(e) => { try { (e.target as HTMLInputElement & { showPicker?: () => void }).showPicker?.(); } catch {} }}
                          className="input-premium text-sm w-32"
                        />
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <label className="text-sm font-medium text-foreground">Duration</label>
                        <div className="flex items-center gap-2">
                          <input type="number" value={registration.morningDuration}
                            onChange={(e) => updateRegistration({ morningDuration: Math.max(5, Math.min(60, parseInt(e.target.value) || 10)) })}
                            min={5} max={60} className="input-premium w-16 text-center text-sm" />
                          <span className="text-sm text-muted-foreground">min</span>
                        </div>
                      </div>
                      <div className="pt-2 border-t border-border space-y-3">
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium text-foreground">Afternoon registration</label>
                          <button
                            type="button"
                            onClick={() => updateRegistration({ afternoonEnabled: !registration.afternoonEnabled })}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${registration.afternoonEnabled ? 'bg-primary' : 'bg-muted'}`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${registration.afternoonEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                          </button>
                        </div>
                        <AnimatePresence>
                          {registration.afternoonEnabled && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="overflow-hidden space-y-3"
                            >
                              <div className="flex items-center justify-between gap-4">
                                <label className="text-sm font-medium text-foreground">Start time</label>
                                <input
                                  type="time"
                                  value={registration.afternoonStartTime}
                                  onChange={(e) => updateRegistration({ afternoonStartTime: e.target.value })}
                                  onClick={(e) => { try { (e.target as HTMLInputElement & { showPicker?: () => void }).showPicker?.(); } catch {} }}
                                  className="input-premium text-sm w-32"
                                />
                              </div>
                              <div className="flex items-center justify-between gap-4">
                                <label className="text-sm font-medium text-foreground">Duration</label>
                                <div className="flex items-center gap-2">
                                  <input type="number" value={registration.afternoonDuration}
                                    onChange={(e) => updateRegistration({ afternoonDuration: Math.max(5, Math.min(60, parseInt(e.target.value) || 10)) })}
                                    min={5} max={60} className="input-premium w-16 text-center text-sm" />
                                  <span className="text-sm text-muted-foreground">min</span>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Gates */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 bg-muted/30 rounded-xl border border-border space-y-2">
                  <label className="flex items-center text-sm font-semibold text-foreground">
                    <LogIn className="w-3.5 h-3.5 mr-1.5 text-primary" />
                    Gates open
                    <HelpTooltip text="Earliest time students can enter the school grounds." />
                  </label>
                  <input type="time" value={periodConfig.schoolStartTime || ''} onChange={(e) => updatePc({ schoolStartTime: e.target.value })}
                    onClick={(e) => { try { (e.target as HTMLInputElement & { showPicker?: () => void }).showPicker?.(); } catch {} }}
                    className="input-premium text-sm w-full" />
                </div>
                <div className="p-4 bg-muted/30 rounded-xl border border-border space-y-2">
                  <label className="flex items-center text-sm font-semibold text-foreground">
                    <LogOut className="w-3.5 h-3.5 mr-1.5 text-primary" />
                    Gates close
                    <HelpTooltip text="When students are officially dismissed from the premises." />
                  </label>
                  <input type="time" value={data.gatesCloseTime || ''} onChange={(e) => onChange('gatesCloseTime', e.target.value)}
                    onClick={(e) => { try { (e.target as HTMLInputElement & { showPicker?: () => void }).showPicker?.(); } catch {} }}
                    className="input-premium text-sm w-full" />
                </div>
              </div>

              {/* Cover deadline */}
              <div className="p-4 bg-muted/30 rounded-xl border border-border space-y-2">
                <label className="flex items-center text-sm font-semibold text-foreground">
                  Cover work submission deadline
                  <span className="text-destructive ml-1">*</span>
                  <HelpTooltip text="Cut-off time for absent teachers to submit cover work. Most UK schools use 7:00–7:30 AM." />
                </label>
                <input type="time" value={data.coverDeadline || ''} onChange={(e) => onChange('coverDeadline', e.target.value)}
                  onClick={(e) => { try { (e.target as HTMLInputElement & { showPicker?: () => void }).showPicker?.(); } catch {} }}
                  className="input-premium text-sm w-32" />
                {data.coverDeadline && <p className="text-xs text-success font-medium">Teachers are reminded to submit cover work by {data.coverDeadline}</p>}
              </div>

              {/* Core period parameters */}
              <div className="p-4 bg-muted/30 rounded-xl border border-border space-y-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Lesson structure</p>

                {/* First lesson start */}
                <div className="flex items-center justify-between gap-4">
                  <label className="text-sm font-medium text-foreground flex items-center gap-1 flex-wrap">
                    First lesson starts at
                    <HelpTooltip text="The time Period 1 (or the first teaching period) begins. If registration is enabled, it starts before this." />
                    {displayedMissingFields.has('schoolStartTime') && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-warning/10 dark:bg-amber-900/40 text-warning-text dark:text-amber-400 text-[10px] font-medium border border-warning/30 dark:border-amber-700/40">
                        <AlertTriangle className="w-2.5 h-2.5" />
                        Not detected
                      </span>
                    )}
                  </label>
                  <input
                    type="time"
                    value={firstLessonStart}
                    onChange={(e) => {
                      updatePc({ schoolStartTime: e.target.value });
                      setScanMissingFields(prev => { const n = new Set(prev); n.delete('schoolStartTime'); return n; });
                    }}
                    onClick={(e) => { try { (e.target as HTMLInputElement & { showPicker?: () => void }).showPicker?.(); } catch {} }}
                    className="input-premium text-sm w-32"
                  />
                </div>

                {/* Period count */}
                <div className="flex items-center justify-between gap-4">
                  <label className="text-sm font-medium text-foreground flex items-center gap-1 flex-wrap">
                    Periods per day
                    {displayedMissingFields.has('numberOfPeriods') && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-warning/10 dark:bg-amber-900/40 text-warning-text dark:text-amber-400 text-[10px] font-medium border border-warning/30 dark:border-amber-700/40">
                        <AlertTriangle className="w-2.5 h-2.5" />
                        Not detected
                      </span>
                    )}
                  </label>
                  <div className="flex items-center gap-2">
                    <button onClick={() => { updatePc({ numberOfPeriods: Math.max(1, numberOfPeriods - 1) }); setScanMissingFields(prev => { const n = new Set(prev); n.delete('numberOfPeriods'); return n; }); }} disabled={numberOfPeriods <= 1}
                      className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center hover:bg-muted/80 disabled:opacity-40 transition-colors">
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="text-lg font-bold text-foreground w-7 text-center">{numberOfPeriods}</span>
                    <button onClick={() => { updatePc({ numberOfPeriods: Math.min(10, numberOfPeriods + 1) }); setScanMissingFields(prev => { const n = new Set(prev); n.delete('numberOfPeriods'); return n; }); }} disabled={numberOfPeriods >= 10}
                      className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center hover:bg-muted/80 disabled:opacity-40 transition-colors">
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Period duration */}
                <div className="flex items-center justify-between gap-4">
                  <label className="text-sm font-medium text-foreground flex items-center gap-1 flex-wrap">
                    Each lesson lasts
                    {displayedMissingFields.has('periodDuration') && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-warning/10 dark:bg-amber-900/40 text-warning-text dark:text-amber-400 text-[10px] font-medium border border-warning/30 dark:border-amber-700/40">
                        <AlertTriangle className="w-2.5 h-2.5" />
                        Not detected, enter manually
                      </span>
                    )}
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={periodDurationDraft}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => setPeriodDurationDraft(e.target.value.replace(/[^0-9]/g, ''))}
                      onBlur={() => {
                        const v = Math.max(15, Math.min(120, parseInt(periodDurationDraft) || 60));
                        updatePc({ periodDuration: v });
                        setScanMissingFields(prev => { const n = new Set(prev); n.delete('periodDuration'); return n; });
                        setPeriodDurationDraft(String(v));
                      }}
                      className="input-premium w-16 text-center text-sm"
                    />
                    <span className="text-sm text-muted-foreground">min</span>
                  </div>
                </div>

                {/* Transfer time */}
                <div className="flex items-center justify-between gap-4">
                  <label className="text-sm font-medium text-foreground flex items-center gap-1">
                    Changeover time between lessons
                    <HelpTooltip text="Time allowed for students to move between classrooms. Typically 3–5 minutes." />
                  </label>
                  <div className="flex items-center gap-2">
                    <input type="number" value={transferTime} onChange={(e) => updatePc({ transferTime: Math.max(0, Math.min(20, parseInt(e.target.value) || 0)) })}
                      min={0} max={20} className="input-premium w-16 text-center text-sm" />
                    <span className="text-sm text-muted-foreground">min</span>
                  </div>
                </div>
              </div>

              {/* Break & Lunch */}
              <div className="p-4 bg-muted/30 rounded-xl border border-border space-y-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Breaks</p>

                {/* Break */}
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <label className="text-sm font-medium text-warning-text dark:text-amber-400 flex items-center gap-1 flex-wrap">
                      Break after period
                      {displayedMissingFields.has('breakConfig') && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-warning/10 dark:bg-amber-900/40 text-warning-text dark:text-amber-400 text-[10px] font-medium border border-warning/30 dark:border-amber-700/40">
                          <AlertTriangle className="w-2.5 h-2.5" />
                          Not detected. Set if used
                        </span>
                      )}
                    </label>
                    <select
                      value={breakAfterPeriod}
                      onChange={(e) => {
                        updatePc({ breakAfterPeriod: parseInt(e.target.value) });
                        setScanMissingFields(prev => { const n = new Set(prev); n.delete('breakConfig'); return n; });
                      }}
                      className="input-premium text-sm min-w-[7rem] flex-shrink-0"
                    >
                      <option value={0}>None</option>
                      {Array.from({ length: numberOfPeriods }, (_, i) => i + 1).map(n => (
                        <option key={n} value={n}>Period {n}</option>
                      ))}
                    </select>
                  </div>
                  {breakAfterPeriod > 0 && (
                    <div className="flex flex-wrap items-center justify-between gap-2 pl-2">
                      <label className="text-sm text-muted-foreground flex items-center gap-1">
                        Break duration
                        {displayedMissingFields.has('breakDuration') && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-warning/10 dark:bg-amber-900/40 text-warning-text dark:text-amber-400 text-[10px] font-medium border border-warning/30 dark:border-amber-700/40">
                            <AlertTriangle className="w-2.5 h-2.5" />
                            Not detected
                          </span>
                        )}
                      </label>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={breakDurationDraft}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => setBreakDurationDraft(e.target.value.replace(/[^0-9]/g, ''))}
                          onBlur={() => {
                            const v = Math.max(5, Math.min(60, parseInt(breakDurationDraft) || 15));
                            updatePc({ breakDurationMins: v });
                            setScanMissingFields(prev => { const n = new Set(prev); n.delete('breakDuration'); return n; });
                            setBreakDurationDraft(String(v));
                          }}
                          className="input-premium w-16 text-center text-sm"
                        />
                        <span className="text-sm text-muted-foreground">min</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Lunch */}
                <div className="space-y-2 pt-2 border-t border-border">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <label className="text-sm font-medium text-warning-text dark:text-orange-400 flex items-center gap-1 flex-wrap">
                      Lunch after period
                      {displayedMissingFields.has('lunchConfig') && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-warning/10 dark:bg-amber-900/40 text-warning-text dark:text-amber-400 text-[10px] font-medium border border-warning/30 dark:border-amber-700/40">
                          <AlertTriangle className="w-2.5 h-2.5" />
                          Not detected. Set if used
                        </span>
                      )}
                    </label>
                    <select
                      value={lunchAfterPeriod}
                      onChange={(e) => {
                        updatePc({ lunchAfterPeriod: parseInt(e.target.value) });
                        setScanMissingFields(prev => { const n = new Set(prev); n.delete('lunchConfig'); return n; });
                      }}
                      className="input-premium text-sm min-w-[7rem] flex-shrink-0"
                    >
                      <option value={0}>None</option>
                      {Array.from({ length: numberOfPeriods }, (_, i) => i + 1).map(n => (
                        <option key={n} value={n}>Period {n}</option>
                      ))}
                    </select>
                  </div>
                  {lunchAfterPeriod > 0 && (
                    <div className="flex flex-wrap items-center justify-between gap-2 pl-2">
                      <label className="text-sm text-muted-foreground flex items-center gap-1">
                        Lunch duration
                        {displayedMissingFields.has('lunchDuration') && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-warning/10 dark:bg-amber-900/40 text-warning-text dark:text-amber-400 text-[10px] font-medium border border-warning/30 dark:border-amber-700/40">
                            <AlertTriangle className="w-2.5 h-2.5" />
                            Not detected
                          </span>
                        )}
                      </label>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={lunchDurationDraft}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => setLunchDurationDraft(e.target.value.replace(/[^0-9]/g, ''))}
                          onBlur={() => {
                            const v = Math.max(15, Math.min(90, parseInt(lunchDurationDraft) || 45));
                            updatePc({ lunchDurationMins: v });
                            setScanMissingFields(prev => { const n = new Set(prev); n.delete('lunchDuration'); return n; });
                            setLunchDurationDraft(String(v));
                          }}
                          className="input-premium w-16 text-center text-sm"
                        />
                        <span className="text-sm text-muted-foreground">min</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Empty state — shown before the user has either
                  uploaded a timetable or chosen to add periods
                  manually. Otherwise we'd be displaying a default
                  formula schedule that doesn't reflect reality and
                  often shows break/period overlap, which reads as
                  "broken first impression" rather than "starting state".  */}
              {!hasUserConfigured && (
                <div className="p-5 rounded-2xl border-2 border-dashed border-border bg-muted/30 text-center space-y-3">
                  <p className="text-sm font-semibold text-foreground">Your school day isn't set up yet</p>
                  <p className="text-xs text-muted-foreground max-w-md mx-auto leading-relaxed">
                    Upload a photo or PDF of your timetable above, we'll fill in the period times for you.
                    Or set them up by hand if you'd rather:
                  </p>
                  <button
                    type="button"
                    onClick={() => setActiveWeekConfig(periodConfig)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-card border border-border text-sm font-semibold text-foreground hover:bg-muted hover:border-foreground/30 transition-colors"
                  >
                    <Clock className="w-4 h-4" />
                    Add periods manually
                  </button>
                </div>
              )}

              {/* Day timeline — tag pills. Only renders after the user
                  has explicitly engaged with the configuration. */}
              {hasUserConfigured && scheduleSlots.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Your school day</p>
                  {scheduleOverlapErrors.length > 0 && (
                    <div className="p-3 rounded-xl bg-destructive/5 border border-destructive/20 space-y-1.5">
                      <p className="flex items-center gap-1.5 text-xs font-semibold text-destructive">
                        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                        Schedule conflicts, fix before continuing
                      </p>
                      {scheduleOverlapErrors.map((w, i) => (
                        <p key={i} className="text-xs text-destructive/80 pl-5">{w}</p>
                      ))}
                    </div>
                  )}
                  <p className="text-[10px] text-muted-foreground">Click any bubble to edit its times.</p>
                  <div className="flex flex-wrap gap-1.5">
                    {scheduleSlots.map((slot, i) => {
                      const isEditing = editingSlotIdx === i;
                      const colour = slotColour[slot.type];
                      if (isEditing) {
                        return (
                          <span
                            key={i}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ring-2 ring-offset-1 ring-primary/40 ${colour}`}
                            // Close when focus leaves the pill entirely
                            onBlur={(e) => {
                              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                                applySlotEdit(i, scheduleSlots, editStart, editEnd);
                                setEditingSlotIdx(null);
                              }
                            }}
                          >
                            <span className="font-semibold whitespace-nowrap">{slot.name}:</span>
                            <input
                              type="time"
                              value={editStart}
                              autoFocus
                              onChange={e => setEditStart(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') { applySlotEdit(i, scheduleSlots, editStart, editEnd); setEditingSlotIdx(null); } }}
                              className="bg-transparent border-none outline-none w-[5.5rem] text-xs"
                            />
                            <span>–</span>
                            <input
                              type="time"
                              value={editEnd}
                              onChange={e => setEditEnd(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') { applySlotEdit(i, scheduleSlots, editStart, editEnd); setEditingSlotIdx(null); } }}
                              className="bg-transparent border-none outline-none w-[5.5rem] text-xs"
                            />
                          </span>
                        );
                      }
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => { setEditingSlotIdx(i); setEditStart(slot.startTime); setEditEnd(slot.endTime); }}
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border cursor-pointer hover:brightness-95 transition-all ${colour}`}
                          title="Click to edit times"
                        >
                          {slot.name}: {slot.startTime}–{slot.endTime}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
                </motion.div>
              </AnimatePresence>
            </div>
          )}

          {/* ── Sub-step 2: Departments + Subjects ───────────────── */}
          {subStep === 2 && (
            <div className="space-y-8">

              {/* Departments */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Departments</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    These populate the department dropdown when adding teachers. Default values are pre-filled. Remove any that don't apply and add your school's own.
                  </p>
                </div>
                <div className="flex gap-2">
                  <input type="text" value={newDepartment} onChange={(e) => setNewDepartment(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addDepartment()}
                    placeholder="Add a department…" className="input-premium flex-1" />
                  <button onClick={addDepartment} disabled={!newDepartment.trim()}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:brightness-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm">
                    <Plus className="w-4 h-4" /><span>Add</span>
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {depts.map((dept, index) => (
                    <motion.span key={`${dept}-${index}`} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium">
                      {dept}
                      <button onClick={() => removeDepartment(index)} className="hover:bg-primary/20 rounded-full p-0.5 transition-colors"><X className="w-3 h-3" /></button>
                    </motion.span>
                  ))}
                </div>
                {depts.length === 0 && <p className="text-xs text-muted-foreground">No departments yet. Add one above.</p>}
              </div>

              {/* Divider */}
              <div className="border-t border-border" />

              {/* Subjects */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Subjects</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Subjects taught at your school. Teachers pick from this list when creating lessons and can be assigned specific subjects.
                  </p>
                </div>
                <div className="flex gap-2">
                  <input type="text" value={newSubject} onChange={(e) => setNewSubject(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addSubject()}
                    placeholder="Type a subject and press Enter or +" className="input-premium flex-1" />
                  <button onClick={addSubject} disabled={!newSubject.trim()}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:brightness-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm">
                    <Plus className="w-4 h-4" /><span>Add</span>
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {subjects.map((subject, index) => (
                    <motion.span key={`${subject}-${index}`} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium">
                      {subject}
                      <button onClick={() => removeSubject(index)} className="hover:bg-primary/20 rounded-full p-0.5 transition-colors"><X className="w-3 h-3" /></button>
                    </motion.span>
                  ))}
                </div>
                {subjects.length === 0 && <p className="text-xs text-muted-foreground">You can also add subjects later from your admin settings.</p>}
              </div>

            </div>
          )}

          {/* ── Sub-step 3: Guest Login ──────────────────────────── */}
          {subStep === 3 && (
            <div className="space-y-4">
              <div className="p-4 bg-muted/30 rounded-xl border border-border space-y-1">
                <div className="flex items-center gap-2">
                  <Monitor className="w-4 h-4 text-primary flex-shrink-0" />
                  <p className="text-sm font-medium text-foreground">Optional: Guest Computer Login</p>
                </div>
                <p className="text-xs text-muted-foreground">
                  If your school has shared computers for cover teachers, add the guest login details here.
                  They'll be shown automatically to cover teachers when they access a lesson.
                </p>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Guest Username</label>
                  <input type="text" value={data.guestAccount?.username || ''}
                    onChange={(e) => onChange('guestAccount', { ...(data.guestAccount || { username: '', password: '', notes: '' }), username: e.target.value })}
                    placeholder="e.g. guest or student" className="input-premium" autoComplete="off" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Guest Password</label>
                  <input type="text" value={data.guestAccount?.password || ''}
                    onChange={(e) => onChange('guestAccount', { ...(data.guestAccount || { username: '', password: '', notes: '' }), password: e.target.value })}
                    placeholder="e.g. School2024!" className="input-premium" autoComplete="new-password" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">
                    Notes for cover teachers
                    <span className="text-muted-foreground font-normal ml-1">(optional)</span>
                  </label>
                  <textarea value={data.guestAccount?.notes || ''}
                    onChange={(e) => onChange('guestAccount', { ...(data.guestAccount || { username: '', password: '', notes: '' }), notes: e.target.value })}
                    placeholder="e.g. Log in as Guest, open Chrome from the desktop…" className="input-premium resize-none" rows={3} />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                You can update these details at any time from <strong>Admin Settings → Guest Login</strong>.
              </p>
            </div>
          )}

        </motion.div>
      </AnimatePresence>
    </WizardStepLayout>
  );
}
